import React, { useEffect, useState } from "react";
import CashEntryServices from "../../../services/CashEntryServices";
import Swal from "sweetalert2";
import Loader from "../Loader/loader";
import Pagination from "../Pagenation/Pagenation";

const getId = (value) =>
  String(value?._id || value?.value || value || "").trim();

const readEntries = (response) => {
  const body = response?.data?.status
    ? response.data
    : response;

  const entries = body?.data?.entries;

  if (!Array.isArray(entries)) {
    throw new Error("Unexpected rejected entries response.");
  }

  return entries;
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const CashRejectedEntriesPanel = ({
  storeId,
  passbookId,
  entryType = "in",
  onProjectionRefresh
}) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [acceptingId, setAcceptingId] = useState("");
  const [reprocessingId, setReprocessingId] = useState("");


  const acceptRejection = async (entry) => {
    if (
      acceptingId ||
      reprocessingId ||
      loading ||
      entry.rejectionAccepted
    ) {
      return;
    }

    // Lock actions while confirmation/request is open.
    setAcceptingId(entry._id);

    let accepted = false;

    try {
      const confirmation = await Swal.fire({
        icon: "warning",
        title: "Accept Rejection?",
        text:
          `Accept rejection for ${entry.requestNumber || "this entry"}? ` +
          "Its amount will be excluded from projection. " +
          "The entry will remain rejected.",
        showCancelButton: true,
        confirmButtonText: "Yes, Accept Rejection",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#198754",
        cancelButtonColor: "#6c757d",
        reverseButtons: true,
        focusCancel: true
      });

      if (!confirmation.isConfirmed) return;

      Swal.fire({
        title: "Accepting Rejection...",
        text: "Please wait.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      await CashEntryServices.acceptRejection(entry._id);

      accepted = true;
      setRefreshKey((previous) => previous + 1);

      await Swal.fire({
        icon: "success",
        title: "Rejection Accepted",
        text: "This amount is now excluded from projection.",
        confirmButtonColor: "#198754"
      });
    } catch (acceptError) {
      await Swal.fire({
        icon: "error",
        title: "Unable to Accept Rejection",
        text:
          acceptError?.response?.data?.message ||
          acceptError?.message ||
          "Please try again.",
        confirmButtonColor: "#dc3545"
      });
    } finally {
      setAcceptingId("");
    }

    if (accepted && onProjectionRefresh) {
      try {
        await onProjectionRefresh();
      } catch {
        await Swal.fire({
          icon: "warning",
          title: "Refresh Required",
          text:
            "Rejection was accepted successfully, but the summary " +
            "could not refresh. Please refresh the passbook viewer.",
          confirmButtonColor: "#0d6efd"
        });
      }
    }
  };

  const handleReprocessRejection = async (entry) => {
    if (
      loading ||
      acceptingId ||
      reprocessingId ||
      entry.rejectionAccepted === true ||
      !["rejectedByAdmin", "rejectedByAccounts"].includes(entry.status)
    ) {
      return;
    }

    setReprocessingId(entry._id);

    let reprocessed = false;

    try {
      const nextReviewer =
        entry.entryType === "in" ? "Accounts" : "CashChecker";

      const confirmation = await Swal.fire({
        icon: "warning",
        title: "Reprocess for Approval?",
        text:
          `${entry.requestNumber || "This entry"} will be sent to ` +
          `${nextReviewer} for approval again. ` +
          "The passbook balance will remain unchanged.",
        input: "textarea",
        inputLabel: "Reprocessing reason (required)",
        inputPlaceholder: "Explain why this entry should be reviewed again",
        inputValidator: (value) =>
          !String(value || "").trim()
            ? "Please enter a reprocessing reason."
            : undefined,
        showCancelButton: true,
        confirmButtonText: "Send for Approval",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#0d6efd",
        allowOutsideClick: false
      });

      if (!confirmation.isConfirmed) return;

      Swal.fire({
        title: "Sending for Approval...",
        text: "Please wait.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading()
      });

      await CashEntryServices.reprocessRejectedEntry(entry._id, {
        reason: String(confirmation.value || "").trim()
      });

      reprocessed = true;

      // It is now pending, so remove it from the rejected list.
      setEntries((previous) =>
        previous.filter(
          (item) => String(item._id) !== String(entry._id)
        )
      );

      setRefreshKey((previous) => previous + 1);

      await Swal.fire({
        icon: "success",
        title: "Sent for Approval",
        text: `Entry sent to ${nextReviewer} for approval again.`,
        confirmButtonColor: "#198754"
      });
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Unable to Reprocess Entry",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again."
      });
    } finally {
      setReprocessingId("");
    }

    if (reprocessed && onProjectionRefresh) {
      try {
        await onProjectionRefresh();
      } catch {
        await Swal.fire({
          icon: "warning",
          title: "Refresh Required",
          text:
            "Entry was sent for approval successfully, but the summary " +
            "could not refresh. Please refresh the passbook viewer."
        });
      }
    }
  };

  const selectedStoreId = getId(storeId);
  const selectedPassbookId = getId(passbookId);

  const [activePage, setActivePage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const totalPages = Math.max(
    1,
    Math.ceil(entries.length / pageSize)
  );

  const currentPage = Math.min(activePage, totalPages - 1);
  const startIndex = currentPage * pageSize;

  const paginatedEntries = entries.slice(
    startIndex,
    startIndex + pageSize
  );

  useEffect(() => {
    setActivePage(0);
  }, [selectedStoreId, selectedPassbookId, entryType, pageSize]);

  useEffect(() => {
    let cancelled = false;

    setEntries([]);

    if (!selectedStoreId || !selectedPassbookId) {
      setLoading(false);
      return;
    }

    const loadEntries = async () => {
      setLoading(true);

      try {
        const statuses = entryType === "out"
          ? ["rejectedByAdmin", "rejectedByAccounts"]
          : ["rejectedByAccounts"];

        const responses = await Promise.all(
          statuses.map((status) =>
            CashEntryServices.getCashEntries({
              storeId: selectedStoreId,
              passbookId: selectedPassbookId,
              entryType,
              status,
              pendingOnly: false
            })
          )
        );

        const rejectedEntries = responses
          .flatMap(readEntries)
          .filter(
            (entry) =>
              statuses.includes(entry.status) &&
              entry.entryType === entryType &&
              getId(entry.store) === selectedStoreId &&
              getId(entry.passbook) === selectedPassbookId
          )
          .sort((first, second) => {
            const getRejectedTime = (entry) =>
              new Date(
                entry.status === "rejectedByAdmin"
                  ? entry.adminReviewedAt || entry.updatedAt
                  : entry.accountsReviewedAt || entry.updatedAt
              ).getTime() || 0;

            return getRejectedTime(second) - getRejectedTime(first);
          });

        if (!cancelled) {
          setEntries(rejectedEntries);
        }
      } catch (loadError) {
        if (!cancelled) {
          Swal.fire({
            icon: "error",
            title: "Unable to Load Rejected Entries",
            text:
              loadError?.response?.data?.message ||
              loadError?.message ||
              "Please try again.",
            confirmButtonColor: "#dc3545"
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadEntries();

    return () => {
      cancelled = true;
    };
  }, [
    selectedStoreId,
    selectedPassbookId,
    entryType,
    refreshKey
  ]);

  if (!selectedStoreId || !selectedPassbookId) {
    return (
      <div className="alert alert-info mb-0">
        Select a Store / Location and passbook to view
        rejected entries.
      </div>
    );
  }

  return (
    <div className="border rounded p-3 bg-white">
      <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
        <h6 className="mb-0">
          Rejected Cash {entryType === "in" ? "In" : "Out"}
          {!loading && ` (${entries.length})`}
        </h6>

        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          disabled={
            loading ||
            Boolean(acceptingId) ||
            Boolean(reprocessingId)
          }
          onClick={() => setRefreshKey((previous) => previous + 1)}
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {loading ? (
        <div className="py-4" role="status" aria-label="Loading rejected entries">
          <Loader />
        </div>
      ) : entries.length === 0 ? (
        <div className="alert alert-info mb-0">
          No rejected Cash {entryType === "in" ? "In" : "Out"} entries found.
        </div>
      ) : (
        <div>
          <div className="d-flex align-items-center gap-2 mb-3">
            <label
              htmlFor={`rejected-page-size-${entryType}`}
              className="mb-0 small"
            >
              Show
            </label>

            <select
              id={`rejected-page-size-${entryType}`}
              className="form-control"
              value={pageSize}
              disabled={
                loading ||
                Boolean(acceptingId) ||
                Boolean(reprocessingId)
              }
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setActivePage(0);
              }}
              style={{ width: 80 }}
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>

            <span className="small">entries</span>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-striped align-middle mb-0 cash-rejected-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Request No.</th>
                  <th>Request Details</th>
                  <th>Rejected Details</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedEntries.map((entry, index) => {
                  const byChecker =
                    entry.status === "rejectedByAdmin";

                  const reviewer = byChecker
                    ? entry.adminReviewedBy
                    : entry.accountsReviewedBy;

                  const reviewedAt = byChecker
                    ? entry.adminReviewedAt
                    : entry.accountsReviewedAt;

                  const rawReason = String(
                    (byChecker ? entry.adminRemark : entry.accountsRemark) ?? ""
                  ).trim();

                  const reason =
                    !rawReason || /^[-–—.\s]+$/.test(rawReason)
                      ? "No Remark"
                      : rawReason;

                  return (
                    <tr key={entry._id}>
                      <td>{startIndex + index + 1}</td>

                      <td className="text-nowrap">
                        <div>{entry.requestNumber || "—"}</div>
                        <div className="d-flex flex-column align-items-start gap-1 mt-1">
                          <span className="badge bg-danger">
                            Rejected
                          </span>

                          <span
                            className={`badge ${entry.rejectionAccepted === true
                              ? "bg-secondary"
                              : "bg-warning text-dark"
                              }`}
                          >
                            {entry.rejectionAccepted === true
                              ? "Rejection Accepted"
                              : "Awaiting Acceptance"}
                          </span>
                        </div>
                      </td>

                      <td className="rejected-request-details">
                        <div>
                          <strong>Entry Date:</strong>{" "}
                          {formatDate(entry.dateTime)}
                        </div>

                        <div>
                          <strong>Category:</strong>{" "}
                          {entry.category?.name || "—"}
                        </div>

                        <div>
                          <strong>Amount (₹):</strong>{" "}
                          {Number(entry.amount || 0).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </div>

                        <div>
                          <strong>Remark:</strong>{" "}
                          <span className="rejected-request-remark">
                            {String(entry.remark || "").trim() || "No Remark"}
                          </span>
                        </div>
                      </td>

                      <td className="rejected-review-details">
                        <div>
                          <strong>Rejected By:</strong>{" "}
                          {byChecker ? "CashChecker" : "Accounts"}
                          {reviewer?.name && <span> ({reviewer.name})</span>}

                          {!byChecker && entry.checkerBypassed && (
                            <span className="badge bg-warning text-dark ms-2">
                              Checker Bypassed
                            </span>
                          )}
                        </div>

                        <div>
                          <strong>Rejected At:</strong>{" "}
                          {formatDate(reviewedAt)}
                        </div>

                        <div>
                          <strong>Rejection Reason:</strong>{" "}
                          <span className="rejected-review-reason">{reason}</span>
                        </div>
                      </td>
                      <td className="text-center">
                        {entry.rejectionAccepted ? (
                          <div>
                            <span className="badge bg-success">
                              Rejection Accepted
                            </span>

                            <div className="small text-muted mt-1">
                              Excluded from projection
                            </div>

                            {entry.rejectionAcceptedAt && (
                              <div className="small text-muted mt-1">
                                {formatDate(entry.rejectionAcceptedAt)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="cash-rejected-actions">
                            <button
                              type="button"
                              className="btn btn-outline-success btn-sm cash-rejected-action"
                              title="Accept Rejection"
                              disabled={
                                loading ||
                                Boolean(acceptingId) ||
                                Boolean(reprocessingId)
                              }
                              onClick={() => acceptRejection(entry)}
                            >
                              {acceptingId === entry._id ? "Accepting..." : "Accept"}
                            </button>

                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm cash-rejected-action"
                              title="Reprocess for Approval"
                              disabled={
                                loading ||
                                Boolean(acceptingId) ||
                                Boolean(reprocessingId)
                              }
                              onClick={() => handleReprocessRejection(entry)}
                            >
                              {reprocessingId === entry._id ? "Sending..." : "Reprocess"}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
            <div className="dataTables_info">
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + pageSize, entries.length)} of{" "}
              {entries.length} entries
            </div>

            <Pagination
              activePage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                if (
                  loading ||
                  acceptingId ||
                  reprocessingId ||
                  !Number.isInteger(page) ||
                  page < 0 ||
                  page >= totalPages
                ) {
                  return;
                }

                setActivePage(page);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CashRejectedEntriesPanel;